import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaApple, FaGooglePlay, FaShieldAlt, FaStar } from 'react-icons/fa';
import { FunctionComponent } from 'react';
import logo from '../assets/logo.png'; // Replace with actual path to EatMeat logo

interface Props {
  isLoggedIn: boolean;
}

const Contact: React.FC<Props> = React.memo(({ isLoggedIn }) => {
  // Define icon components with proper type casting
  const FaAppleIcon = FaApple as FunctionComponent<{ className?: string }>;
  const FaGooglePlayIcon = FaGooglePlay as FunctionComponent<{ className?: string }>;
  const FaShieldAltIcon = FaShieldAlt as FunctionComponent<{ className?: string }>;
  const FaStarIcon = FaStar as FunctionComponent<{ className?: string }>;
  const FaPhoneIcon = FaPhone as FunctionComponent<{ className?: string }>;
  const FaEnvelopeIcon = FaEnvelope as FunctionComponent<{ className?: string }>;
  const FaMapMarkerAltIcon = FaMapMarkerAlt as FunctionComponent<{ className?: string }>;

  return (
    <div className="bg-background min-h-screen">
      <Helmet>
        <title>Contact Us - EatMeat</title>
        <meta name="description" content="Get in touch with EatMeat for support, inquiries, or feedback. Reach us via phone, email, or visit our office in Pleasant Hill, CA." />
      </Helmet>

      {/* Hero Section */}
      <section className="hero-section text-white py-16 sm:py-20 md:py-24 shadow-md">
        <div className="container mx-auto px-6 text-center">
          <div className="app-logo mx-auto mb-6">
            <img src={logo} alt="EatMeat Logo" aria-label="EatMeat Logo" />
          </div>
          <h1 className="hero-heading text-3xl md:text-4xl font-bold">Contact Us</h1>
          <p className="hero-subtitle max-w-2xl mx-auto text-lg mt-4">
            Have questions or need support? Reach out to our team at OdinVistra LLC. We’re here to help you with your carnivore or ketovore journey!
          </p>
        </div>
      </section>

      {/* Contact Information Section */}
      <section className="bg-caption-bg py-20">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-text text-center mb-12">Get in Touch</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="flex flex-col items-center">
              <FaPhoneIcon className="text-primary h-12 w-12 mb-4" />
              <h3 className="text-xl font-semibold text-text mb-2">Phone</h3>
              <p className="text-text"><a href="tel:5102245089" className="hover:text-accent">510-224-5089</a></p>
            </div>
            <div className="flex flex-col items-center">
              <FaEnvelopeIcon className="text-primary h-12 w-12 mb-4" />
              <h3 className="text-xl font-semibold text-text mb-2">Email</h3>
              <p className="text-text"><a href="mailto:info@eatmeatapp.com" className="hover:text-accent">info@eatmeatapp.com</a></p>
            </div>
            <div className="flex flex-col items-center">
              <FaMapMarkerAltIcon className="text-primary h-12 w-12 mb-4" />
              <h3 className="text-xl font-semibold text-text mb-2">Address</h3>
              <p className="text-text text-center">OdinVistra LLC<br />8126 Camelback Place<br />Pleasant Hill, CA 94523</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="bg-background py-16">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-text text-center mb-12">Send Us a Message</h2>
          <div className="max-w-lg mx-auto bg-caption-bg border border-border rounded-lg p-6 shadow-md">
            <p className="text-text mb-4">Please fill out the fields below to send us a message. Our team will get back to you as soon as possible!</p>
            <div className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-text font-semibold mb-2">Name</label>
                <input
                  type="text"
                  id="name"
                  className="w-full p-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
                  placeholder="Your Name"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-text font-semibold mb-2">Email</label>
                <input
                  type="email"
                  id="email"
                  className="w-full p-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
                  placeholder="Your Email"
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-text font-semibold mb-2">Message</label>
                <textarea
                  id="message"
                  className="w-full p-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
                  rows={5}
                  placeholder="Your Message"
                ></textarea>
              </div>
              <button
                className="bg-primary text-background rounded-lg font-semibold py-2 px-4 shadow-md hover:bg-accent focus:outline-none focus:ring-2 focus:ring-accent"
                onClick={() => alert('Form submission is disabled due to sandbox restrictions. Please email info@eatmeatapp.com directly.')}
              >
                Send Message
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Download Section */}
      <section className="bg-secondary-bg py-16">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-text mb-6">Start Your Journey with EatMeat Today!</h2>
          <p className="text-text mb-4 max-w-xl mx-auto">
            Available on iOS and Android. Don’t miss out—download now for a free 7-day personalized meal plan!
          </p>
          <div className="flex justify-center space-x-4 mb-6">
            <div className="trust-badge">
              <FaShieldAltIcon className="text-primary h-5 w-5 mr-2" />
              <span className="text-text">Secure Data</span>
            </div>
            <div className="trust-badge">
              <FaStarIcon className="text-primary h-5 w-5 mr-2" />
              <span className="text-text">Rated 4.8/5</span>
            </div>
          </div>
          <div className="button-container">
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="https://apps.apple.com/app/idcom.carivore.lifestyle.eatmeat"
                target="_blank"
                rel="noopener noreferrer"
                className="download-button flex items-center bg-primary text-background rounded-lg font-semibold hero-button shadow-md pulse focus:outline-none focus:ring-2 focus:ring-accent"
                aria-label="Download EatMeat from the App Store"
              >
                <FaAppleIcon className="mr-2 h-6 w-6" />
                Download Now for Free!
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.carivore.lifestyle.eatmeat"
                target="_blank"
                rel="noopener noreferrer"
                className="download-button flex items-center bg-primary text-background rounded-lg font-semibold hero-button shadow-md pulse focus:outline-none focus:ring-2 focus:ring-accent"
                aria-label="Download EatMeat from Google Play"
              >
                <FaGooglePlayIcon className="mr-2 h-6 w-6" />
                Download Now for Free!
              </a>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
});

export default Contact;